import type { ErrorResponse } from '@1webit/cvs-api-scheme';
import type { PhotoResponse } from '@1webit/cvs-api-scheme/models';
import { queryOptions } from '@tanstack/react-query';

import { DEFAULT_TABLE_PAGE_SIZE } from '@/constants';
import { getResponse, queryParams } from '@/utils';

// user rights
export const getApiV1UserUserRights = (userId: number) => {
  return queryOptions({
    queryKey: ['getApiV1UserUserRights', userId],
    queryFn: () => getResponse('getApiV1UserUserRights', { userId }),
  });
};

export const getLoggedInUserQueryOptions = queryOptions({
  queryKey: ['getApiV1AuthMe'],
  queryFn: () => getResponse('getApiV1AuthMe'),
  // maybe higher stale time?
});

/**
 * For most cases use getApiV1UserBasicInfoId which holds less information. Response of this EP holds sensitive information about user
 * @param id user id
 */
export const getApiV1UserId = (id: number) => {
  return queryOptions({
    queryKey: ['getApiV1UserId', id],
    queryFn: () => getResponse('getApiV1UserId', id),
  });
};

export const getApiV1UserBasicInfoId = (id: number) => queryOptions({
  queryKey: ['getApiV1UserBasicInfoId', id],
  queryFn: () => getResponse('getApiV1UserBasicInfoId', id),
});

export const getApiV1RequestIdLimited = (id: number) => {
  return queryOptions({
    queryKey: ['getApiV1RequestIdLimited', id],
    queryFn: () => getResponse('getApiV1RequestIdLimited', id),
  });
};

// clubs
export const getApiV1UserUserIdClubsMembers = (userId: number, dead = false, clubId?: number, gender?: string, fullText?: string, page = 1, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1UserUserIdClubsMembers', userId, dead, clubId, gender, fullText, page, sort],
    queryFn: async () => {
      let search = '';

      if (gender) {
        search += `gender:${gender};`;
      }

      if (clubId) {
        search += `clubId:${clubId};`;
      }

      const response = await getResponse('getApiV1UserUserIdClubsMembers', userId, queryParams({
        limit: DEFAULT_TABLE_PAGE_SIZE,
        offset: (page - 1) * DEFAULT_TABLE_PAGE_SIZE,
        dead: dead ? 'ANO' : 'NE',
        fullText,
        search,
        sort,
      }));

      return {
        ...response,
        responseData: response.responseData.map(member => ({
          ...member,
          id: member.userId,
        })),
      };
    },
  });
};

// calendar
export const getApiV1UserCalendar = (page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1UserCalendar', page, limit, sort],
    queryFn: async () => {
      const { responseData, ...res } = await getResponse('getApiV1UserCalendar', queryParams({
        sort,
        offset: (page - 1) * limit,
        limit,
      }));
      return {
        ...res,
        responseData: responseData.map(item => ({
          id: item.competitionId,
          ...item,
        })),
      };
    },
  });
};

export const getApiV1UserWhisper = (query: string, gender?: string) => {
  return queryOptions({
    queryKey: ['getApiV1UserWhisper', query, gender],
    queryFn: async () => {
      const response = await getResponse('getApiV1UserWhisper', queryParams({ search: query, gender }));

      return response.map(item => ({
        id: item.userId,
        ...item,
      }));
    },
  });
};

export const getApiV1PhotoUserId = (id: number) => {
  return queryOptions({
    queryKey: ['getApiV1PhotoUserId', id],
    queryFn: () => getResponse('getApiV1PhotoUserId', id) as Promise<PhotoResponse | ErrorResponse>,
  });
};

export const getApiV1CardHistoryUserId = (id: number) => queryOptions({
  queryKey: ['getApiV1CardHistoryUserId', id],
  queryFn: () => getResponse('getApiV1CardHistoryUserId', id),
});

export const getApiV1UserAccessUserId = (id: number) =>
  queryOptions({
    queryKey: ['getApiV1UserAccessUserId', id],
    queryFn: () => getResponse('getApiV1UserAccessUserId', id),
  });
